/**
 * BillingSuccess — landing after Stripe redirects back. Polls the backend for
 * payment_status until 'paid' (or 5 attempts), then shows a celebratory CTA.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const POLL_MS = 2000;
const MAX_ATTEMPTS = 6;

export default function BillingSuccess() {
  /* eslint-disable -- v6 react-hooks/set-state-in-effect + immutability false-positives on the poll-on-mount effect */
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', attempts: 0, data: null });

  const poll = useCallback(async (attempt) => {
    try {
      const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setState({ status: 'paid', data });
        toast.success('Payment received — thank you.');
        return;
      }
      if (data.status === 'expired' || data.payment_status === 'expired') {
        setState({ status: 'expired', data });
        return;
      }
      if (attempt >= MAX_ATTEMPTS) {
        setState({ status: 'timeout', data });
        return;
      }
      setTimeout(() => poll(attempt + 1), POLL_MS);
    } catch {
      setState({ status: 'error', data: null });
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'nosession', data: null });
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-6 py-20">
      <div className="ve-card p-10 max-w-xl text-center">
        {state.status === 'polling' && (
          <>
            <Loader2 size={36} className="mx-auto text-[#5E1F8B] animate-spin"/>
            <h1 className="display text-2xl mt-5">Confirming your payment…</h1>
            <p className="text-sm text-slate-600 mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {state.status === 'paid' && (
          <>
            <CheckCircle2 size={42} className="mx-auto text-emerald-500"/>
            <h1 className="display text-3xl mt-5" data-testid="billing-success-heading">You&apos;re in.</h1>
            <p className="text-sm text-slate-600 mt-3">
              We&apos;ve activated your subscription. A receipt has been emailed; your dashboard is unlocked.
            </p>
            {state.data?.metadata?.plan_name && (
              <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded-full bg-[#FFF8E5] text-[#5E1F8B] border border-[#8B3FC5]">
                {state.data.metadata.plan_name} plan active
              </div>
            )}
            <Link to="/dashboard" data-testid="billing-success-cta" className="mt-8 ve-btn-primary inline-flex">
              Open dashboard <ArrowRight size={14}/>
            </Link>
          </>
        )}
        {(state.status === 'expired' || state.status === 'timeout' || state.status === 'error') && (
          <>
            <XCircle size={42} className="mx-auto text-rose-500"/>
            <h1 className="display text-2xl mt-5">We couldn&apos;t confirm your payment</h1>
            <p className="text-sm text-slate-600 mt-3">
              {state.status === 'timeout'
                ? 'Confirmation is taking longer than expected. You will receive an email once Stripe confirms.'
                : 'The session expired before payment completed. Please try again.'}
            </p>
            <div className="mt-7 flex gap-3 justify-center">
              <Link to="/pricing" className="ve-btn-secondary">Back to pricing</Link>
              <Link to="/dashboard" className="ve-btn-ghost">Go to dashboard <ArrowRight size={14}/></Link>
            </div>
          </>
        )}
        {state.status === 'nosession' && (
          <>
            <XCircle size={42} className="mx-auto text-slate-400"/>
            <h1 className="display text-2xl mt-5">No session found</h1>
            <p className="text-sm text-slate-600 mt-3">Open this page through a Stripe checkout link.</p>
            <Link to="/pricing" className="mt-7 ve-btn-secondary inline-flex">Back to pricing</Link>
          </>
        )}
      </div>
    </main>
  );
}
